import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ChapterHeading, Reveal } from "@/components/Reveal";
import { BatchResult } from "@/components/BatchResult";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useBatchLookup = (t) => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lookup = async (bn) => {
    if (!bn?.trim()) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await axios.get(`${API}/ledger/${encodeURIComponent(bn.trim().toUpperCase())}`);
      setResult(data);
    } catch {
      setError(t("No such batch in the ledger. Perhaps the jar predates our records, or the dog ate the label.",
        "Aucun lot de ce numéro au registre. Peut-être que le pot précède nos archives, ou que le chien a mangé l'étiquette."));
    } finally { setBusy(false); }
  };
  return { result, error, busy, lookup };
};

export const BatchForm = ({ batch, setBatch, onLookup, busy, t }) => (
  <div className="flex gap-3 items-end">
    <div className="flex-1 min-w-0">
      <label htmlFor="ledger-input" className="text-[11px] uppercase tracking-[0.25em] text-[#B5A642]">{t("Batch number", "Numéro de lot")}</label>
      <input id="ledger-input" data-testid="ledger-input" value={batch} onChange={(e) => setBatch(e.target.value.toUpperCase())}
        onKeyDown={(e) => e.key === "Enter" && onLookup()} placeholder="FS-07" autoComplete="off"
        className="apothecary-input w-full mt-3 text-xl font-ledger uppercase" />
    </div>
    <button data-testid="ledger-lookup-button" onClick={onLookup} disabled={busy || !batch.trim()}
      className="px-6 py-3 bg-[#4B3226] text-[#E8DDC7] text-[11px] uppercase tracking-[0.2em] rounded-sm hover:brightness-125 transition-[filter] duration-300 disabled:opacity-40">
      {busy ? t("Consulting…", "Consultation…") : t("Consult", "Consulter")}
    </button>
  </div>
);

export const Ledger = () => {
  const { t, lang } = useLang();
  const [batch, setBatch] = useState("");
  const [samples, setSamples] = useState(false);
  const { result, error, busy, lookup } = useBatchLookup(t);

  useEffect(() => {
    axios.get(`${API}/ledger-status`).then((r) => setSamples(!!r.data?.samples)).catch(() => {});
    const onOpen = (e) => { setBatch(e.detail); lookup(e.detail); };
    window.addEventListener("ledger:open", onOpen);
    return () => window.removeEventListener("ledger:open", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="ledger" data-testid="ledger-section" data-no-translate className="bg-[#111A14] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <ChapterHeading numeral="VIII." title={t("The Batch Ledger", "Le registre des lots")} testid="ledger-heading" />
          <Reveal className="-mt-6">
            <p className="text-base md:text-lg text-[#D3CEC4]/70 leading-relaxed dropcap">
              {t("Every jar carries a batch number on its base. Bring it here and the ledger will tell you where that batch grew, when it was gathered, what the laboratory found in it, and anything else we wrote down, which is everything.",
                "Chaque pot porte un numéro de lot sous sa base. Apportez-le ici et le registre vous dira où ce lot a poussé, quand il a été cueilli, ce que le laboratoire y a trouvé, et tout ce que nous avons noté, c'est-à-dire tout.")}
            </p>
            <p className="mt-4 text-[14px] text-[#D3CEC4]/60">{t("Or scan the little code on the label: it opens the batch's own page.", "Ou balayez le petit code sur l'étiquette : il ouvre la page du lot.")}</p>
            {samples && (
              <p className="mt-4 font-ledger text-[12px] text-[#B5A642]/80" data-testid="ledger-hint">
                {t("Sample entries to try", "Exemples à essayer")}: FS-07 · KM-03 · AS-11 · TV-05 · DD-02
              </p>
            )}
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="border border-[#303B2D] rounded-sm bg-[#161918] p-7 md:p-10">
              <BatchForm batch={batch} setBatch={setBatch} onLookup={() => lookup(batch)} busy={busy} t={t} />
              {error && <p data-testid="ledger-error" className="mt-8 font-display italic text-lg text-[#D3CEC4]/60">{error}</p>}
              {result && (
                <div className="mt-10">
                  <BatchResult result={result} t={t} lang={lang} />
                  <Link to={`/batch/${encodeURIComponent(result.batch_no)}`} data-testid="ledger-batch-page-link"
                    className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#B5A642] hover:text-[#E8DDC7] transition-colors">
                    {t("Open this batch's page", "Ouvrir la page de ce lot")} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
